import { createContext, useContext, useEffect, useMemo, type FC, type ReactNode } from 'react';

import type { IAnalyticsTracker, AnalyticsProviderProps, IChannel } from './types';
import type { ITracker, TrackerConfig } from './trackers/types';
import {
  variantToItem,
  productToItem,
  variantCardToItem,
  checkoutToItems,
  orderToItems,
  checkoutValue,
  checkoutCurrency,
  orderValue,
  orderTax,
  orderShipping,
  purchaseEventId,
} from './data';

import { createGATracker, GAScript } from './trackers/ga';
import { createKlaviyoTracker, KlaviyoScript } from './trackers/klaviyo';
import { createFacebookTracker, FacebookScript } from './trackers/facebook';
import { createInteraktTracker, InteraktScript } from './trackers/interakt';

// No-op tracker for when analytics context is missing
const noopTracker: IAnalyticsTracker = {
  trackPageView: () => {},
  identify: () => {},
  trackViewItem: () => {},
  trackViewItemList: () => {},
  trackViewVariantList: () => {},
  trackAddToCart: () => {},
  trackRemoveFromCart: () => {},
  trackViewCart: () => {},
  trackBeginCheckout: () => {},
  trackAddShippingInfo: () => {},
  trackAddPaymentInfo: () => {},
  trackPurchase: () => {},
  trackAddToWaitlist: () => {},
  trackEvent: () => {},
  trackPurchaseDirect: () => {},
};

const AnalyticsContext = createContext<IAnalyticsTracker>(noopTracker);

export const useAnalytics = () => useContext(AnalyticsContext);

interface CreateTrackerCoordinatorProps {
  channel: IChannel;
  trackers: ITracker[];
  debug?: boolean;
}

const createTrackerCoordinator = ({
  channel,
  trackers,
  debug,
}: CreateTrackerCoordinatorProps): IAnalyticsTracker => {
  const defaultCurrency = channel.defaultCurrency;

  const dispatch = <T extends keyof ITracker>(
    method: T,
    props: Parameters<ITracker[T]>[0]
  ) => {
    trackers.forEach((tracker) => {
      try {
        (tracker[method] as (props: unknown) => void)(props);
      } catch (error) {
        if (debug) {
          console.error(`[Analytics] Error in ${method}:`, error);
        }
      }
    });
  };

  return {
    trackPageView({ url, title }) {
      dispatch('trackPageView', { url, title });
    },

    identify({ email, data }) {
      dispatch('identify', { email, data });
    },

    trackViewItem({ variant, itemUrl }) {
      const item = variantToItem(variant, itemUrl, defaultCurrency);
      dispatch('trackViewItem', { item });
    },

    trackViewItemList({ products, itemUrl, listId, listName }) {
      const items = products.map((product, index) =>
        productToItem(product, itemUrl, defaultCurrency, index)
      );
      dispatch('trackViewItemList', { items, listId, listName });
    },

    trackViewVariantList({ variants, itemUrl, listId, listName }) {
      const items = variants.map((variant, index) =>
        variantCardToItem(variant, itemUrl, defaultCurrency, index)
      );
      dispatch('trackViewItemList', { items, listId, listName });
    },

    trackAddToCart({ variant, quantity, itemUrl, checkout }) {
      const item = variantToItem(variant, itemUrl, defaultCurrency);
      const value = checkout
        ? checkoutValue(checkout) + item.price * quantity
        : item.price * quantity;
      dispatch('trackAddToCart', { item, quantity, value });
    },

    trackRemoveFromCart({ variant, quantity, itemUrl }) {
      const item = variantToItem(variant, itemUrl, defaultCurrency);
      const value = item.price * quantity;
      dispatch('trackRemoveFromCart', { item, quantity, value });
    },

    trackViewCart({ checkout, itemUrl }) {
      const items = checkoutToItems(checkout, itemUrl);
      const value = checkoutValue(checkout);
      dispatch('trackViewCart', { items, value });
    },

    trackBeginCheckout({ checkout, itemUrl }) {
      const items = checkoutToItems(checkout, itemUrl);
      const value = checkoutValue(checkout);
      dispatch('trackBeginCheckout', { items, value });
    },

    trackAddShippingInfo({ order, itemUrl }) {
      const items = orderToItems(order, itemUrl);
      const value = orderValue(order);
      dispatch('trackAddShippingInfo', { items, value });
    },

    trackAddPaymentInfo({ order, itemUrl }) {
      const items = orderToItems(order, itemUrl);
      const value = orderValue(order);
      dispatch('trackAddPaymentInfo', { items, value });
    },

    trackPurchase({ order, itemUrl }) {
      const items = orderToItems(order, itemUrl);
      const value = orderValue(order);
      const tax = orderTax(order);
      const shipping = orderShipping(order);
      dispatch('trackPurchase', {
        transactionId: order.id,
        items,
        value,
        tax,
        shipping,
      });
    },

    trackAddToWaitlist({ email, variant, itemUrl, checkout }) {
      const item = variantToItem(variant, itemUrl, defaultCurrency);
      const value = checkout
        ? checkoutValue(checkout) + item.price
        : item.price;
      dispatch('trackAddToWaitlist', { email, item, value });
    },

    trackEvent({ event, data }) {
      dispatch('trackEvent', { event, data });
    },

    trackPurchaseDirect({ transactionId, items, value, tax, shipping }) {
      dispatch('trackPurchase', { transactionId, items, value, tax, shipping });
    },
  };
};

export const AnalyticsProvider: FC<AnalyticsProviderProps> = ({
  children,
  channel,
  gaId,
  klaviyoId,
  fbPixelId,
  interaktToken,
  debug,
}) => {
  const trackerConfig: TrackerConfig = useMemo(
    () => ({ channel, debug }),
    [channel, debug]
  );

  const trackers = useMemo(() => {
    const result: ITracker[] = [];

    if (gaId) {
      result.push(createGATracker(gaId, trackerConfig));
    }
    if (klaviyoId) {
      result.push(createKlaviyoTracker(klaviyoId, trackerConfig));
    }
    if (fbPixelId) {
      result.push(createFacebookTracker(fbPixelId, trackerConfig));
    }
    if (interaktToken) {
      result.push(createInteraktTracker(interaktToken, trackerConfig));
    }

    return result;
  }, [gaId, klaviyoId, fbPixelId, interaktToken, trackerConfig]);

  // Initialize all trackers
  useEffect(() => {
    trackers.forEach((tracker) => tracker.init());
  }, [trackers]);

  const coordinator = useMemo(
    () => createTrackerCoordinator({ channel, trackers, debug }),
    [channel, trackers, debug]
  );

  return (
    <AnalyticsContext.Provider value={coordinator}>
      {gaId && <GAScript measurementId={gaId} />}
      {klaviyoId && <KlaviyoScript companyId={klaviyoId} />}
      {fbPixelId && <FacebookScript pixelId={fbPixelId} />}
      {interaktToken && <InteraktScript token={interaktToken} />}
      {children}
    </AnalyticsContext.Provider>
  );
};
